import { localeCompare } from "@/helpers/logicHelpers";
import type {
	ExtractParameterGeneric,
	Parameter,
	ParameterTypes,
} from "@/models/Parameter";

export const useGenericParameters = <T extends Record<string, Parameter<any>>>(
	dateString: string,
	parameters: T,
) => {
	return new Proxy(parameters, {
		get(...args) {
			const [, prop] = args;
			const parameter = Reflect.get(...args);

			return parameter.variables
				? new Proxy(parameter.variables, {
						get(...args) {
							const parameter = Reflect.get(...args);
							return fromParameterValues(parameter?.values);
						},
					})
				: fromParameterValues(parameter.values);

			function fromParameterValues(parameterValues: any) {
				let selectedMoment: string | undefined;
				let parameterValue: any;

				if (parameterValues) {
					const availableMoments =
						Object.keys(parameterValues).sort(localeCompare);

					selectedMoment = availableMoments
						.filter((moment) => moment <= dateString)
						.pop();

					if (selectedMoment) {
						parameterValue = parameterValues[selectedMoment]?.value;
					}
				}

				if (selectedMoment === undefined) {
					throw new Error(
						`Parameter ${String(prop)} not available on ${dateString}.`,
					);
				}

				if (parameterValue === undefined || parameterValue === null) {
					throw new Error(
						`Parameter ${String(
							prop,
						)} not available on ${dateString}: canceled since ${selectedMoment}.`,
					);
				}

				return parameterValue;
			}
		},
	}) as {
		[K in keyof T]: T[K] extends {
			variables: Record<string, { values: any }>;
		}
			? {
					[Var in keyof T[K]["variables"]]: ExtractParameterGeneric<
						T[K]["variables"][Var]
					>;
				}
			: ParameterTypes<typeof parameters>[K];
	};
};
